import { Fragment, useEffect, useState } from "react";
import getComponent from "../../_Case/ComponentsList";
import classnames from "classnames";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import repository from "core/repository";
import { FormattedMessage, useIntl } from "react-intl";
import ReactSelectStyles from "components/formcomponents/ReactSelectStyles";
import {
  addValidationError,
  removeValidationError,
} from "redux/actions/saveComponentValueAction";
import { RootState } from "redux/reducers/rootReducer";
import {
  EndUserOption,
  loadEndUsers,
} from "pages/admin-app/end-user/services/loadEndUsers";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { EndUser } from "pages/admin-app/end-user/models/response/get-end-users-response";
import {
  VendorsOption,
  loadVendors,
} from "pages/admin-app/vendor/services/loadVendors";
import { Distributor } from "pages/admin-app/distributor/models/response/create-distributor-response";
import { getEndUser } from "pages/admin-app/end-user/services/end-user-service";

interface AppUser {
  id: string;
  name: string;
  email: string;
}

interface FormValue {
  endUser: EndUser | null;
  distributor: Distributor | any | null;
  vendor: VendorsOption | null;
  approver: AppUser | null | undefined;
}

interface ValidationErrorProps {
  key: string;
  valid: boolean;
  description: string;
}

interface Props {
  myName: string;
  getValue: (path: string) => FormValue | null;
  setValue: (value: FormValue, path: string) => void;
  parentSetValue?: FormValue | null;
  parentHandleSave?: (value: FormValue, path: string) => void;
  formComponentData: {
    args: Array<{
      key: keyof FormValue | "territoryManager" | "industrySpecialist";
      type: string;
      renderType?: string;
      translate?: boolean;
      description?: string;
      required?: boolean;
      responsive?: string;
      infoMessage?: string;
    }>;
  };
}

const EndUserComponent = ({
  myName,
  getValue,
  setValue,
  parentSetValue,
  parentHandleSave,
  formComponentData,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [endUser, setEndUser] = useState<EndUser | null>(null);
  const [selectedEndUser, setSelectedEndUser] = useState<EndUserOption | null>(
    null
  );
  const [distributor, setDistributor] = useState<Distributor | any | null>(
    null
  );
  const [distributorDisplay, setDistributorDisplay] = useState<
    Distributor | unknown | null
  >(null);
  const [territoryManager, setTerritoryManager] = useState("");
  const [industrySpecialist, setIndustrySpecialist] = useState("");
  const [vendor, setVendor] = useState<VendorsOption | null>(null);
  const [approver, setApprover] = useState<AppUser | null | undefined>(null);
  const [allusers, setAllUsers] = useState<AppUser[]>([]);

  const endUserValidation = useSelector(
    (state: RootState) =>
      state?.saveComponentValue?.validationErrors.find(
        (ve: ValidationErrorProps) => ve.key === `${myName}.endUser`
      ),
    shallowEqual
  );
  const currentUser = useSelector((state: RootState) => state.currentUser);

  const setStateFormValue = (value: FormValue) => {
    setEndUser(value.endUser);
    setDistributor(value.distributor);
    setDistributorDisplay(
      value.distributor
        ? !value.distributor.isCustom
          ? {
              isLocked: true,
              value: `${value.distributor.name} ${value.distributor.city}, ${value.distributor.state}`,
            }
          : { isLocked: false, value: value.distributor.value }
        : null
    );
    setTerritoryManager(
      value.endUser
        ? value.endUser.territoryManager
          ? value.endUser.territoryManager.name
          : ""
        : ""
    );
    setIndustrySpecialist(
      value.endUser
        ? value.endUser.industrySpecialist
          ? value.endUser.industrySpecialist.name
          : ""
        : ""
    );
    setVendor(value.vendor);
  };

  useEffect(() => {
    const loadUsers = async () => {
      const response = await repository.getListsUsers();
      if (response.data) {
        setAllUsers(response.data);
      }
    };

    loadUsers().catch((err) => console.log(err));
  }, [currentUser]);

  useEffect(() => {
    let loadedValue = null;
    if (parentSetValue !== undefined && parentSetValue !== null) {
      loadedValue = parentSetValue;
      if (!loadedValue) {
        loadedValue = null;
      }
    } else {
      loadedValue = getValue(myName);
    }

    if (loadedValue) {
      setStateFormValue(loadedValue);
      if (loadedValue.endUser) {
        const eu = loadedValue.endUser;

        // If endUser was saved without country data, fetch it from the API
        if (eu.id && !eu.country) {
          getEndUser({ endUserId: eu.id })
            .then((response) => {
              if (response?.country) {
                const enrichedEndUser = { ...eu, country: response.country };
                setEndUser(enrichedEndUser);
                setSelectedEndUser({
                  value: enrichedEndUser.id,
                  label: `${enrichedEndUser.name}, ${enrichedEndUser.city}`,
                  data: enrichedEndUser,
                });
              }
            })
            .catch((err) => console.log("Failed to fetch end user country:", err));
        }

        setSelectedEndUser({
          value: eu.id,
          label: `${eu.name}, ${eu.city}`,
          data: eu,
        });
      }
      if (loadedValue.vendor && loadedValue.vendor.value) {
        setVendor(loadedValue.vendor as VendorsOption);
      }
      if (loadedValue.approver) {
        setApprover(loadedValue.approver);
      }
    }
  }, [parentSetValue, getValue, myName]);

  //We use any due to the fact that Distributor accepts "isLocked" and "value", which are not part of the original Distributor
  const handleChange = (
    value: EndUserOption | VendorsOption | AppUser[] | any,
    name: string
  ) => {
    let resultName = name.split(".").pop();
    let objectToSave: FormValue = { endUser, distributor, vendor, approver };

    if (resultName === "endUser") {
      if (value) {
        const euOption = value as EndUserOption;
        objectToSave = {
          ...objectToSave,
          endUser: euOption?.data,
          distributor: distributor
            ? distributor.isCustom
              ? euOption.data.distributor
              : distributor
            : euOption.data.distributor,
        };
        setSelectedEndUser(value);
        if (
          allusers &&
          value.data.industrySpecialist &&
          value.data.industrySpecialist.email
        ) {
          const foundUser = allusers.find(
            (u: AppUser) =>
              u.email.toLocaleLowerCase() ===
              value.data.industrySpecialist?.email.toLocaleLowerCase()
          );
          objectToSave = { ...objectToSave, approver: foundUser };
          setApprover(foundUser);
        }
        dispatch(removeValidationError(`${myName}.endUser`));
      } else {
        objectToSave = {
          ...objectToSave,
          endUser: null,
          distributor: distributor
            ? !distributor.isCustom
              ? null
              : distributor
            : null,
        };
        setSelectedEndUser(value);
        dispatch(
          addValidationError({
            key: `${myName}.endUser`,
            valid: false,
            description: "End User",
          })
        );
      }
    } else if (resultName === "distributor") {
      if (value.isLocked) {
        objectToSave.distributor = endUser ? endUser.distributor : null;
      } else {
        objectToSave = {
          ...objectToSave,
          distributor: { isCustom: true, value: value.value },
        };
      }
      if ((value.value && value.value.length > 0) || value.isLocked) {
        dispatch(removeValidationError(`${myName}.distributor`));
      } else {
        dispatch(
          addValidationError({
            key: `${myName}.distributor`,
            valid: false,
            description: "Distributor",
          })
        );
      }
    } else if (resultName === "vendor") {
      if (value) {
        const vOpt = value as VendorsOption;

        objectToSave = {
          ...objectToSave,
          vendor: vOpt,
        };

        dispatch(removeValidationError(`${myName}.vendor`));

        setVendor(vOpt);
      } else {
        objectToSave = { ...objectToSave, vendor: null };
        dispatch(
          addValidationError({
            key: `${myName}.vendor`,
            valid: false,
            description: "Vendor",
          })
        );
        setVendor(null);
      }
    }

    setStateFormValue(objectToSave);
    if (parentHandleSave !== undefined) {
      parentHandleSave(objectToSave, myName);
    } else {
      setValue(objectToSave, myName);
    }
  };

  const renderEndUserDropdown = (
    <>
      <label className="input-shell__label input-shell__label--dark input-shell__label--required">
        <FormattedMessage id="endUser" />
      </label>
      <AsyncPaginate
        onChange={(opt) => handleChange(opt, `${myName}.endUser`)}
        placeholder={intl.formatMessage({ id: "enduser.placeholder" })}
        className="select__element"
        value={selectedEndUser}
        styles={ReactSelectStyles}
        loadOptions={loadEndUsers as LoadOptions<any, any>}
        isClearable
        formatOptionLabel={(opt) => (
          <div className="option">
            <strong>{opt.label}</strong>
            <div className="sub">
              <span className="header">
                {intl.formatMessage({ id: "tm" })}:
              </span>
              {opt.data.territoryManager?.name}
            </div>
            <div className="sub">
              <span className="header">
                {intl.formatMessage({ id: "is" })}:
              </span>
              {opt.data.industrySpecialist?.name}
            </div>
          </div>
        )}
      />
    </>
  );

  const renderVendorDropdown = (
    <>
      <label className="input-shell__label input-shell__label--dark input-shell__label">
        <FormattedMessage id="vendor" />
      </label>
      <AsyncPaginate
        onChange={(opt) => handleChange(opt, `${myName}.vendor`)}
        placeholder={intl.formatMessage({ id: "vendor.placeholder" })}
        value={vendor}
        styles={ReactSelectStyles}
        loadOptions={loadVendors as LoadOptions<any, any>}
        formatOptionLabel={(opt) => (
          <div>
            <strong>{opt.label}</strong>
            <div className="sub">{opt.data?.contactName}</div>
          </div>
        )}
        isClearable
      />
    </>
  );

  return formComponentData.args.map((fc) => {
    return (
      <Fragment key={fc.key}>
        {fc.key === "endUser" && (
          <div
            className={classnames(
              "endUser",
              "group",
              "componentContainer-linebreak",
              {
                invalid: !!endUserValidation && !endUserValidation.valid,
              }
            )}
          >
            <div className="input-shell">
              <div className="input-shell__container">
                {renderEndUserDropdown}
              </div>
            </div>
          </div>
        )}
        {fc.key === "vendor" && (
          <div
            className={classnames(
              "endUser",
              "group",
              "componentContainer-linebreak"
            )}
          >
            <div className="input-shell">
              <div className="input-shell__container">
                {renderVendorDropdown}
              </div>
            </div>
          </div>
        )}
        {fc.key === "territoryManager" &&
          territoryManager &&
          getComponent(
            fc,
            `${myName}.territoryManager`,
            handleChange,
            territoryManager
          )}
        {fc.key === "industrySpecialist" &&
          industrySpecialist &&
          getComponent(
            fc,
            `${myName}.industrySpecialist`,
            handleChange,
            industrySpecialist
          )}
        {fc.key === "distributor" &&
          distributor !== null &&
          getComponent(
            fc,
            `${myName}.distributor`,
            handleChange,
            distributorDisplay
          )}
      </Fragment>
    );
  });
};

export default EndUserComponent;
